import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getAllProgramas } from "../../../services/programa.service";
import { getAllIdiomas } from "../../../services/idioma.service";
import { actualizarPrograma } from "../../../services/programa.service";
import { crearProgramaIdioma, eliminarProgramaIdioma, getAllProgramaIdiomas } from "../../../services/programaIdioma.service";
import { ContenedorPageTemplate } from "../../../components/templates/ContenedorPageTemplate";
import { showAlert, showErrorAlert } from "../../../components/molecules/ShowAlert";

export const GestionarIdiomasProgramaPage = () => {
  const navigate = useNavigate();
  const [programas, setProgramas] = useState([]);
  const [idiomasDisponibles, setIdiomasDisponibles] = useState([]);
  const [programaEditando, setProgramaEditando] = useState(null);
  const [idiomaSeleccionado, setIdiomaSeleccionado] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [terminoBusqueda, setTerminoBusqueda] = useState("");

  const recargarDatos = async () => {
    try {
      setLoading(true);
      const [dataProgramas, dataIdiomas, dataProgramaIdiomas] = await Promise.all([
        getAllProgramas(),
        getAllIdiomas(),
        getAllProgramaIdiomas()
      ]);
      
      // Agrupar idiomas por programa
      const idiomasPorPrograma = {};
      dataProgramaIdiomas.forEach(pi => {
        if (!idiomasPorPrograma[pi.idPrograma]) {
          idiomasPorPrograma[pi.idPrograma] = [];
        }
        idiomasPorPrograma[pi.idPrograma].push(pi.idIdioma);
      });
      
      // Agregar idiomas a cada programa
      const programasConIdiomas = dataProgramas.map(programa => ({
        ...programa,
        idiomas: idiomasPorPrograma[programa.idPrograma] || []
      }));
      

      
      setProgramas(programasConIdiomas || []);
      setIdiomasDisponibles(dataIdiomas || []);
    } catch (err) {
      setError("No se pudieron cargar los datos. Por favor, verifica el servidor.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    recargarDatos();
  }, []);

  const obtenerIdiomasPrograma = (programa) => {
    // Ahora los idiomas vienen directamente en programa.idiomas como array de IDs
    if (programa.idiomas && Array.isArray(programa.idiomas)) {
      return programa.idiomas.map(id => String(id));
    }
    
    return [];
  };

  const obtenerNombreIdioma = (idiomaId) => {
    const idioma = idiomasDisponibles.find(i => 
      String(i.idIdioma ?? i.id) === String(idiomaId)
    );
    return idioma?.nombre ?? idioma?.descripcion ?? `Idioma ${idiomaId}`;
  };

  const getIdiomasDisponiblesParaAgregar = (programa) => {
    const idiomasActuales = obtenerIdiomasPrograma(programa);
    return idiomasDisponibles.filter(idioma => {
      const idiomaId = String(idioma.idIdioma ?? idioma.id);
      return !idiomasActuales.includes(idiomaId);
    });
  };

  const handleEditar = (programa) => {
    setProgramaEditando(programa);
    setIdiomaSeleccionado("");
  };

  const handleCancelarEdicion = () => {
    setProgramaEditando(null);
    setIdiomaSeleccionado("");
  };

  const handleAgregarIdioma = () => {
    if (!idiomaSeleccionado || !programaEditando) return;

    const idiomasActuales = obtenerIdiomasPrograma(programaEditando);
    if (!idiomasActuales.includes(idiomaSeleccionado)) {
      const nuevosIdiomas = [...idiomasActuales, idiomaSeleccionado];
      
      setProgramas(prev => prev.map(p => 
        p.idPrograma === programaEditando.idPrograma 
          ? { ...p, idiomas: nuevosIdiomas }
          : p
      ));
      
      setProgramaEditando(prev => ({ ...prev, idiomas: nuevosIdiomas }));
      setIdiomaSeleccionado("");
    }
  };

  const handleEliminarIdioma = (idiomaId) => {
    if (!programaEditando) return;

    const idiomasActuales = obtenerIdiomasPrograma(programaEditando);
    const nuevosIdiomas = idiomasActuales.filter(id => id !== idiomaId);
    
    setProgramas(prev => prev.map(p => 
      p.idPrograma === programaEditando.idPrograma 
        ? { ...p, idiomas: nuevosIdiomas }
        : p
    ));
    
    setProgramaEditando(prev => ({ ...prev, idiomas: nuevosIdiomas }));
  };

  const handleGuardar = async () => {
    if (!programaEditando) return;

    try {
      const idiomasActuales = obtenerIdiomasPrograma(programaEditando);
      
      // Obtener las relaciones actuales del programa
      const programaIdiomasActuales = await getAllProgramaIdiomas();
      const relacionesActuales = programaIdiomasActuales.filter(
        pi => pi.idPrograma === programaEditando.idPrograma
      );
      
      // Obtener los IDs de idiomas actuales
      const idiomasActualesIds = relacionesActuales.map(pi => String(pi.idIdioma));
      
      // Encontrar idiomas a agregar (están en idiomasActuales pero no en idiomasActualesIds)
      const idiomasAAgregar = idiomasActuales.filter(
        id => !idiomasActualesIds.includes(id)
      );
      
      // Encontrar idiomas a eliminar (están en idiomasActualesIds pero no en idiomasActuales)
      const idiomasAEliminar = idiomasActualesIds.filter(
        id => !idiomasActuales.includes(id)
      );
      
      console.log("Idiomas actuales en el programa:", idiomasActuales);
      console.log("Idiomas actuales en la BD:", idiomasActualesIds);
      console.log("Idiomas a agregar:", idiomasAAgregar);
      console.log("Idiomas a eliminar:", idiomasAEliminar);
      
      // Crear nuevas relaciones
      const crearPromises = idiomasAAgregar.map(idiomaId => {
        const dataToSend = {
          idPrograma: programaEditando.idPrograma,
          idIdioma: idiomaId // Enviar como string, no como número
        };
        console.log("Enviando datos para crear relación:", dataToSend);
        return crearProgramaIdioma(dataToSend);
      });
      
      // Eliminar relaciones existentes
      const eliminarPromises = idiomasAEliminar.map(idiomaId => {
        const relacionAEliminar = relacionesActuales.find(
          pi => String(pi.idIdioma) === idiomaId
        );
        return eliminarProgramaIdioma(relacionAEliminar.idProgramaIdioma);
      });
      
      // Ejecutar todas las operaciones
      await Promise.all([...crearPromises, ...eliminarPromises]);
      
      // Recargar los datos después de guardar exitosamente
      await recargarDatos();
      
      showAlert({
        title: "¡Guardado correctamente!",
        text: "Los idiomas del programa han sido actualizados.",
      });
      
      setProgramaEditando(null);
      setIdiomaSeleccionado("");
    } catch (err) {
      console.error("Error completo:", err);
      console.error("Error response:", err.response);
      
      if (err.response?.data?.errors) {
        const mensajes = Object.values(err.response.data.errors).flat();
        showErrorAlert("Errores de validación:\n" + mensajes.join("\n"));
      } else if (err.response?.data?.message) {
        showErrorAlert("Error del servidor: " + err.response.data.message);
      } else {
        showErrorAlert("Ocurrió un error al guardar los cambios. Verifica la consola para más detalles.");
      }
    }
  };

  if (loading) return (
    <ContenedorPageTemplate titulo="Gestionar Idiomas de Programas">
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    </ContenedorPageTemplate>
  );
  
  if (error) return (
    <ContenedorPageTemplate titulo="Gestionar Idiomas de Programas">
      <div className="flex justify-center items-center h-64">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      </div>
    </ContenedorPageTemplate>
  );

  return (
    <ContenedorPageTemplate titulo="Gestionar Idiomas de Programas">
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
        <div className="max-w-6xl mx-auto">
          {/* Header simplificado */}
          <div className="flex items-center justify-between mb-6">
            <div className="bg-blue-100 px-4 py-2 rounded-lg">
              <span className="text-sm text-blue-600 font-medium">
                {terminoBusqueda 
                  ? `${programas.filter(p => 
                      p.nombre?.toLowerCase().includes(terminoBusqueda.toLowerCase())
                    ).length} de ${programas.length} programas`
                  : `${programas.length} programa${programas.length !== 1 ? 's' : ''}`}
              </span>
            </div>
            <button
              onClick={recargarDatos}
              disabled={loading}
              className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Recargar datos"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
              </svg>
            </button>
          </div>

          {/* Buscador */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl shadow-md border border-blue-100 p-5 mb-6">
            <div className="flex items-center gap-3">
              <div className="flex-1 relative group">
                <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
                  <svg 
                    className="w-6 h-6 text-blue-500 group-focus-within:text-blue-600 transition-colors duration-200" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Buscar programa por nombre..."
                  value={terminoBusqueda}
                  onChange={(e) => setTerminoBusqueda(e.target.value)}
                  className="w-full pl-12 pr-4 py-3.5 bg-white border-2 border-blue-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-gray-800 placeholder-gray-400 shadow-sm hover:border-blue-300"
                />
                {terminoBusqueda && (
                  <button
                    onClick={() => setTerminoBusqueda("")}
                    className="absolute inset-y-0 right-0 flex items-center pr-4 text-gray-400 hover:text-red-500 transition-colors duration-200"
                    type="button"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
            {terminoBusqueda && (
              <div className="mt-3 flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm">
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-100 text-blue-700 font-medium">
                    <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd" />
                    </svg>
                    {programas.filter(p => 
                      p.nombre?.toLowerCase().includes(terminoBusqueda.toLowerCase())
                    ).length} resultado{programas.filter(p => 
                      p.nombre?.toLowerCase().includes(terminoBusqueda.toLowerCase())
                    ).length !== 1 ? 's' : ''} encontrado{programas.filter(p => 
                      p.nombre?.toLowerCase().includes(terminoBusqueda.toLowerCase())
                    ).length !== 1 ? 's' : ''}
                  </span>
                  <span className="text-gray-500">
                    de {programas.length} programa{programas.length !== 1 ? 's' : ''} total{programas.length !== 1 ? 'es' : ''}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Programas Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {programas
              .filter(programa => 
                !terminoBusqueda || 
                programa.nombre?.toLowerCase().includes(terminoBusqueda.toLowerCase())
              )
              .map((programa) => {
              const idiomasPrograma = obtenerIdiomasPrograma(programa);
              const estaEditando = programaEditando?.idPrograma === programa.idPrograma;
              
              return (
                <div 
                  key={programa.idPrograma}
                  className={`bg-white rounded-2xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl ${
                    estaEditando ? 'ring-4 ring-blue-200 transform scale-105' : 'hover:scale-105'
                  }`}
                >
                  {/* Header de la tarjeta */}
                  <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="text-xl font-bold mb-2">{programa.nombre}</h3>
                        <div className="flex items-center space-x-4 text-sm">
                          <span className="flex items-center">
                            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                            </svg>
                            {programa.duracion}
                          </span>
                          <span className="flex items-center">
                            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                            </svg>
                            Bs. {programa.totalPrecio}
                          </span>
                        </div>
                      </div>
                      {!estaEditando ? (
                        <button 
                          onClick={() => handleEditar(programa)}
                          className="bg-white text-blue-600 px-4 py-2 rounded-lg font-semibold hover:bg-blue-50 transition-colors duration-200 flex items-center"
                        >
                          <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                          </svg>
                          Gestionar
                        </button>
                      ) : (
                        <div className="flex space-x-2">
                          <button 
                            onClick={handleGuardar}
                            className="bg-green-500 text-white px-3 py-2 rounded-lg font-semibold hover:bg-green-600 transition-colors duration-200 flex items-center"
                          >
                            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            Guardar
                          </button>
                          <button 
                            onClick={handleCancelarEdicion}
                            className="bg-red-500 text-white px-3 py-2 rounded-lg font-semibold hover:bg-red-600 transition-colors duration-200 flex items-center"
                          >
                            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                            Cancelar
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Contenido de la tarjeta */}
                  <div className="p-6">
                    {/* Idiomas asignados */}
                    <div className="mb-6">
                      <h4 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                        <svg className="w-5 h-5 mr-2 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                        </svg>
                        Idiomas Asignados
                      </h4>
                      
                      {idiomasPrograma.length === 0 ? (
                        <div className="bg-gray-50 rounded-lg p-4 text-center">
                          <svg className="w-8 h-8 mx-auto text-gray-400 mb-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                          <p className="text-gray-500 font-medium">Ningún idioma asignado</p>
                          <p className="text-sm text-gray-400">Haz clic en "Gestionar" para agregar idiomas</p>
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {idiomasPrograma.map((idiomaId) => (
                            <span 
                              key={idiomaId}
                              className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 border border-green-200"
                            >
                              {obtenerNombreIdioma(idiomaId)}
                              {estaEditando && (
                                <button
                                  type="button"
                                  onClick={() => handleEliminarIdioma(idiomaId)}
                                  className="ml-2 inline-flex items-center justify-center w-4 h-4 rounded-full bg-red-500 text-white hover:bg-red-600 transition-colors duration-200"
                                >
                                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                  </svg>
                                </button>
                              )}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Sección de agregar idioma */}
                    {estaEditando && (
                      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                        <h5 className="text-sm font-semibold text-blue-800 mb-3 flex items-center">
                          <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                          </svg>
                          Agregar Nuevo Idioma
                        </h5>
                        <div className="flex gap-3">
                          <select
                            value={idiomaSeleccionado}
                            onChange={(e) => setIdiomaSeleccionado(e.target.value)}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                          >
                            <option value="">Selecciona un idioma</option>
                            {getIdiomasDisponiblesParaAgregar(programa).map((idioma) => {
                              const value = String(idioma.idIdioma ?? idioma.id);
                              return (
                                <option key={value} value={value}>
                                  {idioma.nombre ?? idioma.descripcion ?? `Idioma ${value}`}
                                </option>
                              );
                            })}
                          </select>
                          <button
                            type="button"
                            onClick={handleAgregarIdioma}
                            disabled={!idiomaSeleccionado}
                            className={`px-4 py-2 rounded-lg font-semibold transition-all duration-200 flex items-center ${
                              idiomaSeleccionado 
                                ? 'bg-blue-600 text-white hover:bg-blue-700' 
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            }`}
                          >
                            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                            </svg>
                            Agregar
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Mensaje si no hay programas */}
          {programas.length === 0 && (
            <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
              <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">No hay programas disponibles</h3>
              <p className="text-gray-600">Primero necesitas crear algunos programas para poder gestionar sus idiomas.</p>
            </div>
          )}

          {/* Mensaje si no hay resultados de búsqueda */}
          {programas.length > 0 && terminoBusqueda && programas.filter(p => 
            p.nombre?.toLowerCase().includes(terminoBusqueda.toLowerCase())
          ).length === 0 && (
            <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
              <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
              </svg>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">No se encontraron programas</h3>
              <p className="text-gray-600">No hay programas que coincidan con "{terminoBusqueda}". Intenta con otro término de búsqueda.</p>
            </div>
          )}
        </div>
      </div>
    </ContenedorPageTemplate>
  );
};
